/**
 * AssistantPane — the full-screen assistant, wired to the UNIVERSAL ACTION ROUTER
 * (design doc §10). Each send calls assistantAct (POST /api/assistant/act), which
 * classifies the message and either takes a domain action, answers a question, asks
 * for clarification, or reports that an owner must approve.
 *
 * Result handling (mirrors GlobalAssistantDock so the two surfaces behave identically):
 *   - 'action'        → append a confirmation turn ("✓ summary"); navigate to the entity
 *                       (handled by useAssistantAction via routeForTarget).
 *   - 'answer'        → append the answer content as an assistant turn.
 *   - 'clarification' → append the clarification as an assistant turn; the user replies.
 *   - 'needs_owner'   → append the reason + owner-approval note.
 *
 * Maintains a local conversation history for display only (the action router is
 * stateless per call; context.route is passed each turn).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useRef, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { usePathname } from 'expo-router';
import { Text, Card, Field, Button } from '../ui';
import { MicButton } from '../voice/MicButton';
import { theme } from '../../theme';
import { useAssistantAction } from './useAssistantAction';
import type { AssistantActionResult } from '../../api/assistant';

interface Props {
  /**
   * Kept for call-site compatibility (the screen passes the org id). The action
   * router derives scope from the JWT server-side, so this is not sent in the body.
   */
  tenancyUnitId: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  /** Visual treatment for non-answer assistant turns. */
  tone?: 'action' | 'clarification' | 'needs_owner';
  /** Guided-intake progress hint (spec 3): "Step X of Y" under a clarification. */
  progress?: { step: number; total: number };
}

/** Turn the envelope into a displayable assistant message. */
function messageForResult(result: AssistantActionResult): Message {
  switch (result.kind) {
    case 'action':
      return { role: 'assistant', content: `✓ ${result.summary}`, tone: 'action' };
    case 'answer':
      return { role: 'assistant', content: result.content ?? result.summary };
    case 'clarification':
      return {
        role: 'assistant',
        content: result.clarification ?? result.summary,
        tone: 'clarification',
        ...(result.progress ? { progress: result.progress } : {}),
      };
    case 'needs_owner':
      return {
        role: 'assistant',
        content: `${result.reason ?? result.summary}\n\nAn owner has to approve this before it can be applied.`,
        tone: 'needs_owner',
      };
    default:
      return { role: 'assistant', content: result.summary };
  }
}

export function AssistantPane(_props: Props) {
  const pathname = usePathname();
  const { loading, error, submit, clearError } = useAssistantAction();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const scrollRef = useRef<ScrollView>(null);

  async function handleSend() {
    const question = input.trim();
    if (!question || loading) return;

    const userMessage: Message = { role: 'user', content: question };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    clearError();

    // Scroll to bottom after adding the user message.
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50);

    // Send the recent conversation so a follow-up (or a reply to the assistant's own clarification)
    // keeps context. `messages` here is the history BEFORE this turn (setMessages is async).
    const result = await submit(question, {
      route: pathname ?? '',
      history: messages.slice(-10).map((m) => ({ role: m.role, content: m.content })),
    });
    if (!result) {
      // Error is rendered by the hook; drop the optimistic user turn so the user can retry.
      setMessages((prev) => prev.slice(0, -1));
      return;
    }
    setMessages((prev) => [...prev, messageForResult(result)]);
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50);
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 88 : 0}
    >
      {/* Conversation */}
      <ScrollView
        ref={scrollRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          messages.length === 0 && styles.scrollContentEmpty,
        ]}
        keyboardShouldPersistTaps="handled"
      >
        {messages.length === 0 ? (
          <View style={styles.emptyState}>
            <Text variant="title">BEAM Assistant</Text>
            <Text variant="body" color="secondary" style={styles.centered}>
              Tell me what to do and I'll do it — create a task, schedule a visit, log a
              receipt, start an estimate — or ask me anything about your jobs.
            </Text>
          </View>
        ) : (
          messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} />
          ))
        )}

        {loading && (
          <View style={styles.loadingBubble} accessibilityLabel="Assistant is responding">
            <ActivityIndicator size="small" color={theme.color.text.secondary} />
            <Text variant="body" color="secondary">Thinking…</Text>
          </View>
        )}

        {error ? (
          <View accessibilityRole="alert">
            <Card style={styles.errorCard}>
              <Text variant="small" color="danger">{error}</Text>
            </Card>
          </View>
        ) : null}
      </ScrollView>

      {/* Input row */}
      <View style={styles.inputRow}>
        <Field
          value={input}
          onChangeText={(t) => {
            setInput(t);
            if (error) clearError();
          }}
          placeholder="Tell me what to do, or ask a question…"
          multiline
          maxLength={4000}
          editable={!loading}
          returnKeyType="send"
          blurOnSubmit={false}
          onSubmitEditing={handleSend}
          accessibilityLabel="Assistant question input"
          containerStyle={styles.fieldContainer}
          style={styles.fieldInput}
        />
        <MicButton
          onTranscribe={(t) => setInput((prev) => (prev ? `${prev} ${t}` : t))}
          disabled={loading}
          label=""
          size="md"
          accessibilityLabel="Dictate assistant question"
        />
        <Button
          title="Send"
          variant="primary"
          size="md"
          fullWidth={false}
          onPress={handleSend}
          loading={loading}
          disabled={!input.trim() || loading}
          accessibilityLabel="Send question"
        />
      </View>
    </KeyboardAvoidingView>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';

  // Tone-specific surface for action confirmations / clarifications / owner-gated replies.
  const toneStyle =
    message.tone === 'action'
      ? styles.bubbleAction
      : message.tone === 'clarification'
        ? styles.bubbleClarify
        : message.tone === 'needs_owner'
          ? styles.bubbleOwner
          : null;

  return (
    <View
      style={[styles.bubbleWrapper, isUser ? styles.bubbleWrapperUser : styles.bubbleWrapperAssistant]}
      accessibilityRole="text"
      accessibilityLabel={`${isUser ? 'You' : 'Assistant'}: ${message.content}`}
    >
      {!isUser && message.tone === 'needs_owner' ? (
        <Text variant="label" color="secondary">NEEDS OWNER APPROVAL</Text>
      ) : null}
      <View
        style={[
          styles.bubble,
          isUser ? styles.bubbleUser : styles.bubbleAssistant,
          toneStyle,
        ]}
      >
        <Text variant="body" color={isUser ? 'onPrimary' : 'primary'}>
          {message.content}
        </Text>
      </View>
      {!isUser && message.progress ? (
        <Text variant="label" color="secondary" accessibilityLabel={`Step ${message.progress.step} of ${message.progress.total}`}>
          {`Gathering details… step ${message.progress.step} of ${message.progress.total}`}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: theme.layout.screenPadding,
    paddingTop: theme.layout.screenPadding,
    paddingBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  scrollContentEmpty: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
    gap: theme.spacing.sm,
  },
  centered: {
    textAlign: 'center',
  },
  bubbleWrapper: {
    maxWidth: '80%',
    gap: theme.spacing.xs,
  },
  bubbleWrapperUser: {
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
  },
  bubbleWrapperAssistant: {
    alignSelf: 'flex-start',
    alignItems: 'flex-start',
  },
  bubble: {
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  bubbleUser: {
    backgroundColor: theme.color.action.primary,
    borderBottomRightRadius: theme.spacing.xs,
  },
  bubbleAssistant: {
    backgroundColor: theme.color.bg.surface,
    borderBottomLeftRadius: theme.spacing.xs,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
  },
  bubbleAction: {
    backgroundColor: theme.status.paid.bg,
    borderColor: theme.color.border.default,
  },
  bubbleClarify: {
    backgroundColor: theme.status.inProgress.bg,
    borderColor: theme.color.border.default,
  },
  bubbleOwner: {
    backgroundColor: theme.status.sent.bg,
    borderColor: theme.color.border.default,
  },
  loadingBubble: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.md,
    borderBottomLeftRadius: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
  },
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.color.action.danger,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
  fieldContainer: {
    flex: 1,
  },
  fieldInput: {
    maxHeight: 120,
    textAlignVertical: 'top',
  },
});
